import { useParams } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { useAdminUser } from '../lib/admin';
import { ApiError } from '../lib/api';
import { AdminUserDetail } from './AdminUserDetail';
import { ErrorState } from './ErrorState';
import { PageHeader } from './PageHeader';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// One person's admin page (`/admin/users/:id`, #5727) — container. Mounted ONLY
// inside <AdminGate/>: the read is a staff read the server audits
// (`admin.tenant_read`), so it is not asked until the gate admits the session.
//
// A 404 is an ANSWER — no user has that id — so it renders as its own line
// rather than the boundary's error box, whose Retry would 404 forever (the
// `AdminRunPanel` rule).

export function AdminUserPanel(props: { readonly token: string | null }): JSX.Element {
  const params = useParams<{ id: string }>();
  const user = useAdminUser(() => params.id ?? null, props.token);
  const notFound = (): boolean =>
    user.isError && user.error instanceof ApiError && user.error.status === 404;

  return (
    <>
      <PageHeader title={t('admin.user_title')} desc={t('admin.user_subtitle')} />
      <Show when={!notFound()} fallback={<ErrorState message={t('admin.user_not_found')} />}>
        <QueryBoundary query={user} loading={<Skeleton height="12rem" />}>
          {(row) => (
            <AdminUserDetail user={row} token={props.token} onChanged={() => void user.refetch()} />
          )}
        </QueryBoundary>
      </Show>
    </>
  );
}
